import { useMemo } from "react";

import type {
  HumanCard,
  HumanDecisionDetail,
  HumanFacetBucket,
  HumanGroupDimension,
  HumanProvenanceResult,
} from "../../api/humanProvenance";
import type { DagViewerNode } from "./types";
import {
  humanCardFromNode,
  humanDecisionNodeId,
  humanEvidenceNodeId,
  humanJournalNodeId,
} from "./humanAdapter";
import type { HumanFilterState } from "./humanViewState";
import {
  ALL_HUMAN_RECORD_KINDS,
  DEFAULT_HUMAN_FILTERS,
  humanFilterCount,
} from "./humanViewState";
import "./human-provenance.css";

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });

function formatInstant(value: string): string {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? dateTime.format(new Date(parsed)) : value;
}

function humanize(value: string): string {
  return value.split("_").join(" ");
}

function cardNodeId(card: HumanCard): string {
  if (card.entity_kind === "decision") return humanDecisionNodeId(card.ref.id);
  if (card.entity_kind === "journal_entry") return humanJournalNodeId(card.ref.id);
  return humanEvidenceNodeId(card.ref.decision_id, card.ref.artifact_version_id);
}

function cardActor(card: HumanCard): { label: "Creator" | "Author"; id: string } {
  return card.entity_kind === "journal_entry"
    ? { label: "Author", id: card.author_actor_id }
    : { label: "Creator", id: card.creator_actor_id };
}

function cardRole(card: HumanCard): "Decision" | "Evidence" | "Journal" {
  if (card.entity_kind === "decision") return "Decision";
  if (card.entity_kind === "decision_evidence") return "Evidence";
  return "Journal";
}

function cardName(card: HumanCard): string {
  if (card.entity_kind === "decision") return `${card.decision_key} · ${card.technical_claim_name}`;
  if (card.entity_kind === "decision_evidence") return card.evidence_name;
  return card.content_name;
}

function cardClassification(card: HumanCard): string {
  if (card.entity_kind === "decision") return humanize(card.current_status);
  if (card.entity_kind === "decision_evidence") return humanize(card.evidence_type.value);
  return humanize(card.entry_type);
}

function cardsForResult(result: HumanProvenanceResult): HumanCard[] {
  if (result.items.length) return result.items;
  const detail = result.detail;
  if (!detail) return [];
  if (detail.entity_kind === "decision") return [detail, ...detail.evidence.items];
  if (detail.entity_kind === "decision_evidence") return [detail, detail.parent_decision];
  return [detail, ...(detail.related_decision ? [detail.related_decision] : [])];
}

interface NodeAvailability {
  has(id: string): boolean;
}

function RefButton({
  id,
  label,
  onSelect,
  availableNodeIds,
  requiresExactTechnicalNode = false,
}: {
  id: string;
  label: string;
  onSelect?: (id: string) => void;
  availableNodeIds?: NodeAvailability;
  requiresExactTechnicalNode?: boolean;
}) {
  const available = !requiresExactTechnicalNode || availableNodeIds?.has(id) === true;
  if (!available || !onSelect) {
    return (
      <span className="human-provenance__ref is-unavailable" data-reference-available={available ? "true" : "false"}>
        <span>{label}</span><code>{id.slice(id.indexOf(":") + 1)}</code>
        {!available ? <small>Not present at this exact cut</small> : null}
      </span>
    );
  }
  return (
    <button type="button" className="human-provenance__ref" onClick={() => onSelect(id)}>
      <span>{label}</span><code>{id.slice(id.indexOf(":") + 1)}</code>
    </button>
  );
}

export function HumanProvenanceTable({
  result,
  selectedId,
  onSelect,
}: {
  result: HumanProvenanceResult;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}) {
  const cards = useMemo(() => cardsForResult(result), [result]);
  const decisionDetail = result.detail?.entity_kind === "decision" ? result.detail : null;
  const completeOrdinals = useMemo(() => new Map(
    decisionDetail?.evidence.disclosure === "complete"
      ? decisionDetail.evidence.items.map((item) => [cardNodeId(item), item.ordinal] as const)
      : [],
  ), [decisionDetail]);
  if (!cards.length) return null;
  return (
    <section className="human-provenance__table" aria-label="Human provenance records">
      <header>
        <div><span>Human provenance</span><h3>Decisions, evidence, and journals</h3></div>
        <time dateTime={result.temporal_cut.effective_as_of}>Valid at {formatInstant(result.temporal_cut.effective_as_of)}</time>
      </header>
      <div tabIndex={0} className="human-provenance__table-scroll">
        <table>
          <caption>Permission-visible human provenance in server-authoritative order</caption>
          <thead><tr><th scope="col">Role</th><th scope="col">Record</th><th scope="col">Creator / author</th><th scope="col">Classification</th><th scope="col">Sensitivity</th><th scope="col">Validity</th></tr></thead>
          <tbody>
            {cards.map((card) => {
              const id = cardNodeId(card);
              const actor = cardActor(card);
              const ordinal = completeOrdinals.get(id);
              return (
                <tr key={id} className={selectedId === id ? "is-selected" : undefined}>
                  <td><strong>{cardRole(card)}</strong>{ordinal === undefined ? null : <small>Stored order {ordinal + 1}</small>}</td>
                  <th scope="row"><button type="button" aria-pressed={selectedId === id} onClick={() => onSelect?.(id)}>{cardName(card)}</button></th>
                  <td><span>{actor.label}</span><code>{actor.id}</code></td>
                  <td>{cardClassification(card)}</td>
                  <td>{card.sensitivity.tier_name}</td>
                  <td>{humanize(card.validity.lifecycle_state)}</td>
                </tr>
              );
            })}
            {decisionDetail?.evidence.disclosure === "partial" ? (
              <tr className="is-restricted" data-testid="human-evidence-restriction-row">
                <td><strong>Restricted</strong></td>
                <th scope="row" colSpan={5}>Additional evidence is restricted. Hidden counts, positions, and metadata are not disclosed.</th>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DecisionEvidence({ detail, onSelect }: { detail: HumanDecisionDetail; onSelect?: (id: string) => void }) {
  const evidence = detail.evidence;
  return (
    <section className="human-provenance__evidence" aria-label="Decision evidence">
      <header><span>Evidence disclosure</span><strong>{evidence.disclosure === "complete" ? "Complete ordered evidence" : "Visible evidence · partial chain"}</strong></header>
      {evidence.disclosure === "complete" ? (
        <ol>
          {evidence.items.map((item) => (
            <li key={item.ref.artifact_version_id} value={item.ordinal + 1}>
              <button type="button" onClick={() => onSelect?.(humanEvidenceNodeId(item.ref.decision_id, item.ref.artifact_version_id))}>
                <strong>{item.evidence_name}</strong><span>{humanize(item.evidence_type.value)} · stored order {item.ordinal + 1}</span>
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <>
          <ul>
            {evidence.items.map((item) => (
              <li key={item.ref.artifact_version_id}>
                <button type="button" onClick={() => onSelect?.(humanEvidenceNodeId(item.ref.decision_id, item.ref.artifact_version_id))}>
                  <strong>{item.evidence_name}</strong><span>{humanize(item.evidence_type.value)}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="human-provenance__restricted" role="status" data-testid="human-evidence-restriction">
            Additional evidence is restricted. No hidden count, position, identity, type, sensitivity, or validity metadata is disclosed.
          </p>
        </>
      )}
    </section>
  );
}

export function HumanRecordInspector({
  node,
  result,
  onSelect,
  availableNodeIds,
}: {
  node: DagViewerNode;
  result: HumanProvenanceResult | null;
  onSelect?: (id: string) => void;
  availableNodeIds?: NodeAvailability;
}) {
  const card = humanCardFromNode(node);
  if (!card) {
    if (node.metadata.human_restriction_marker === "restricted_evidence_omitted") {
      return (
        <section className="human-provenance__inspector is-restricted" aria-label="Restricted evidence marker">
          <header><span>Permission boundary</span><h3>Additional evidence is restricted</h3></header>
          <p>No hidden count, position, identity, type, sensitivity, or validity metadata is disclosed.</p>
        </section>
      );
    }
    const event = node.metadata.human_status_event;
    if (node.semanticRole === "decision_status" && event && typeof event === "object") {
      const status = event as { status: string; actor_id: string; effective_at: string; recorded_at: string };
      return (
        <section className="human-provenance__inspector is-status" aria-label="Decision status event details">
          <header><span>Decision history</span><h3>Status · {humanize(status.status)}</h3></header>
          <dl>
            <div><dt>Actor</dt><dd><code>{status.actor_id}</code></dd></div>
            <div><dt>Effective / event</dt><dd>{formatInstant(status.effective_at)}</dd></div>
            <div><dt>Recorded / system</dt><dd>{formatInstant(status.recorded_at)}</dd></div>
          </dl>
        </section>
      );
    }
    return null;
  }
  const detail = result?.detail;
  if (card.entity_kind === "decision") {
    const exact = detail?.entity_kind === "decision" && detail.ref.id === card.ref.id ? detail : null;
    return (
      <section className="human-provenance__inspector is-decision" aria-label="Decision details" data-testid="human-decision-inspector">
        <header><span>Decision</span><h3>{card.decision_key}</h3><strong>{humanize(card.current_status)}</strong></header>
        <p>{card.technical_claim_name}</p>
        <dl>
          <div><dt>Creator</dt><dd><code>{card.creator_actor_id}</code></dd></div>
          <div><dt>Sensitivity</dt><dd>{card.sensitivity.tier_name}</dd></div>
          <div><dt>Validity</dt><dd>{humanize(card.validity.lifecycle_state)}</dd></div>
          <div><dt>Effective / event</dt><dd>{formatInstant(card.effective_at)}</dd></div>
          <div><dt>Recorded / system</dt><dd>{formatInstant(card.recorded_at)}</dd></div>
        </dl>
        <RefButton
          id={`artifact_version:${card.technical_claim_ref.id}`}
          label="Technical claim ArtifactVersion"
          onSelect={onSelect}
          availableNodeIds={availableNodeIds}
          requiresExactTechnicalNode
        />
        {exact ? (
          <>
            <section className="human-provenance__history" aria-label="Decision status history">
              <header><span>Status history</span><strong>As of the exact cut</strong></header>
              <ol>
                {exact.status_history.map((event) => (
                  <li key={event.ref.id}>
                    <button type="button" onClick={() => onSelect?.(`decision_status_event:${event.ref.id}`)}>
                      <strong>{humanize(event.status)}</strong><span>{formatInstant(event.effective_at)}</span><code>{event.actor_id}</code>
                    </button>
                  </li>
                ))}
              </ol>
            </section>
            <DecisionEvidence detail={exact} onSelect={onSelect} />
          </>
        ) : null}
      </section>
    );
  }
  if (card.entity_kind === "decision_evidence") {
    const exact = detail?.entity_kind === "decision_evidence"
      && detail.ref.decision_id === card.ref.decision_id
      && detail.ref.artifact_version_id === card.ref.artifact_version_id ? detail : null;
    return (
      <section className="human-provenance__inspector is-evidence" aria-label="Decision evidence details" data-testid="human-evidence-inspector">
        <header><span>Evidence</span><h3>{card.evidence_name}</h3><strong>{humanize(card.evidence_type.value)}</strong></header>
        <dl>
          <div><dt>Creator</dt><dd><code>{card.creator_actor_id}</code></dd></div>
          <div><dt>Decision status</dt><dd>{humanize(card.decision_status)}</dd></div>
          <div><dt>Sensitivity</dt><dd>{card.sensitivity.tier_name}</dd></div>
          <div><dt>Validity</dt><dd>{humanize(card.validity.lifecycle_state)}</dd></div>
          {exact?.parent_evidence_disclosure === "complete" ? <div><dt>Stored order</dt><dd>{(exact.ordinal ?? 0) + 1}</dd></div> : null}
        </dl>
        <RefButton id={humanDecisionNodeId(card.decision_ref.id)} label="Decision" onSelect={onSelect} />
        <RefButton
          id={`artifact_version:${card.evidence_artifact_version_ref.id}`}
          label="Evidence ArtifactVersion"
          onSelect={onSelect}
          availableNodeIds={availableNodeIds}
          requiresExactTechnicalNode
        />
        {exact?.parent_evidence_disclosure === "partial" ? (
          <p className="human-provenance__restricted" role="status">The parent evidence chain is partial. This record’s stored position is not disclosed.</p>
        ) : null}
      </section>
    );
  }
  return (
    <section className="human-provenance__inspector is-journal" aria-label="Journal entry details" data-testid="human-journal-inspector">
      <header><span>Journal</span><h3>{card.content_name}</h3><strong>{humanize(card.entry_type)}</strong></header>
      <dl>
        <div><dt>Author</dt><dd><code>{card.author_actor_id}</code></dd></div>
        <div><dt>Sensitivity</dt><dd>{card.sensitivity.tier_name}</dd></div>
        <div><dt>Validity</dt><dd>{humanize(card.validity.lifecycle_state)}</dd></div>
        <div><dt>Effective / event</dt><dd>{formatInstant(card.effective_at)}</dd></div>
        <div><dt>Recorded / system</dt><dd>{formatInstant(card.recorded_at)}</dd></div>
      </dl>
      <RefButton
        id={`artifact_version:${card.subject_ref.id}`}
        label="Subject ArtifactVersion"
        onSelect={onSelect}
        availableNodeIds={availableNodeIds}
        requiresExactTechnicalNode
      />
      <RefButton
        id={`artifact_version:${card.content_artifact_version_ref.id}`}
        label="Immutable journal content"
        onSelect={onSelect}
        availableNodeIds={availableNodeIds}
        requiresExactTechnicalNode
      />
      <RefButton
        id={`operation_run:${card.publication_operation_ref.id}`}
        label="Publication OperationRun"
        onSelect={onSelect}
        availableNodeIds={availableNodeIds}
        requiresExactTechnicalNode
      />
      {card.decision_ref ? <RefButton id={humanDecisionNodeId(card.decision_ref.id)} label="Related decision" onSelect={onSelect} /> : null}
    </section>
  );
}

const FACETS: Array<{
  dimension: HumanGroupDimension;
  field: keyof HumanFilterState;
  label: string;
}> = [
  { dimension: "record_kind", field: "recordKinds", label: "Human record type" },
  { dimension: "actor", field: "actorIds", label: "Actor · creator / author / status" },
  { dimension: "decision_status", field: "decisionStatuses", label: "Decision status" },
  { dimension: "evidence_type", field: "evidenceTypes", label: "Evidence type" },
  { dimension: "entry_type", field: "entryTypes", label: "Journal entry type" },
  { dimension: "sensitivity_tier", field: "sensitivityTierIds", label: "Sensitivity" },
  { dimension: "lifecycle_state", field: "lifecycleStates", label: "Validity state" },
];

function toggle(values: readonly string[], value: string): string[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

export function HumanFacetFilters({
  filters,
  buckets,
  effectiveAsOf,
  isLoading,
  retentionLimitReached,
  error,
  onChange,
  onClose,
}: {
  filters: HumanFilterState;
  buckets: ReadonlyMap<HumanGroupDimension, readonly HumanFacetBucket[]>;
  effectiveAsOf: string;
  isLoading?: boolean;
  retentionLimitReached?: boolean;
  error?: Error | null;
  onChange: (filters: HumanFilterState) => void;
  onClose?: () => void;
}) {
  return (
    <aside className="human-provenance__filters" aria-label="Human provenance filters" data-testid="human-provenance-filters">
      <header><div><span>Permission-safe facets</span><h2>Filter human provenance</h2></div>{onClose ? <button type="button" onClick={onClose} aria-label="Close human provenance filters">×</button> : null}</header>
      <p>Counts include visible records only. Validity is evaluated at <time dateTime={effectiveAsOf}>{formatInstant(effectiveAsOf)}</time>.</p>
      {isLoading ? <p role="status">Loading permission-safe facets…</p> : null}
      {retentionLimitReached ? (
        <p role="status">Facet safety limit reached. Narrow the exact filters to inspect additional visible values.</p>
      ) : null}
      {error ? <p role="alert">Human facets unavailable: {error.message}</p> : null}
      {FACETS.map(({ dimension, field, label }) => {
        const options = (buckets.get(dimension) ?? []).filter((bucket) => (
          dimension !== "record_kind"
            || ALL_HUMAN_RECORD_KINDS.includes(bucket.key as (typeof ALL_HUMAN_RECORD_KINDS)[number])
        ));
        const selected = filters[field] as readonly string[];
        return (
          <fieldset key={dimension}>
            <legend>{label}</legend>
            {options.length ? <div>{options.map((bucket) => (
              <button
                type="button"
                key={bucket.key}
                aria-pressed={selected.includes(bucket.key)}
                className={selected.includes(bucket.key) ? "is-active" : undefined}
                disabled={field === "recordKinds" && selected.length === 1 && selected.includes(bucket.key)}
                onClick={() => {
                  const next = toggle(selected, bucket.key);
                  if (field === "recordKinds") {
                    if (!next.length) return;
                    onChange({
                      ...filters,
                      recordKinds: ALL_HUMAN_RECORD_KINDS.filter((kind) => next.includes(kind)),
                    });
                    return;
                  }
                  onChange({ ...filters, [field]: next });
                }}
              ><span>{bucket.label}</span><small>{bucket.count}</small></button>
            ))}</div> : <small>No visible facet values</small>}
          </fieldset>
        );
      })}
      <footer><span>{humanFilterCount(filters)} active filter groups</span><button type="button" onClick={() => onChange({ ...DEFAULT_HUMAN_FILTERS })}>Clear human filters</button></footer>
    </aside>
  );
}
